import React, { useState, useCallback } from "react";
import "./issuer-nav.css";
import {
  RefreshIconV1,
  MenuIconV1,
  CoinsIconV1,
  InfoIconV1,
  LogOutIconV1,
  KeyIconV1,
} from "images/image-component";
import BrandLogo from "components/brand-logo/BrandLogo";
import RolePill from "components/role-pill/RolePill";
import Button from "components/button/Button";
import SettingsDrawer, {
  DrawerItem,
  DrawerSection,
} from "components/settings-drawer/SettingsDrawer";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import { useAuth } from "auth";
import { useNavigate } from "react-router-dom";
import { getNetworkConfig, NETWORK_NAME, NetworkType } from "utils/network";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { useTokenSupport } from "features/tokens/useTokenSupport";
import { tokenLabel } from "utils/tokens";

interface Props {
  networkType?: NetworkType;
}

function IssuerNav({ networkType }: Props) {
  const network = getNetworkConfig(networkType);
  const queryClient = useQueryClient();
  const isFetching = useIsFetching();
  const { signOut } = useAuth();
  const {
    tokens,
    selected: selectedToken,
    label: tokenSymbol,
    param: tokenAddress,
    canSwitch,
    select: selectToken,
  } = useSettlementToken();
  // The portal chose the token; only the deploy knows whether its registry holds it. `undefined`
  // means "not answered yet" and shows nothing — an unreachable backend is not evidence.
  const { data: tokenSupport } = useTokenSupport(tokenAddress);
  const tokenIssue =
    tokenSupport?.supported === false
      ? `${tokenSymbol} is not enabled on this network`
      : tokenSupport?.supported && tokenSupport.enabled === false
        ? `${tokenSymbol} is paused for new payments`
        : undefined;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  const handleRefresh = () => {
    queryClient.invalidateQueries();
  };

  const handleLogout = useCallback(async () => {
    await signOut();
    navigate("/");
  }, [signOut, navigate]);

  const tokenDrawerItems: DrawerItem[] = tokens.map((token) => ({
    label: tokenLabel(token),
    icon: <CoinsIconV1 />,
    selected: token.address === selectedToken?.address,
    onClick: () => selectToken(token.address),
  }));

  const settingsSections: DrawerSection[] = [
    {
      label: "Preferences",
      // Absent, not disabled, when the network offers only one token.
      items: canSwitch
        ? [
            {
              label: "Network token",
              icon: <CoinsIconV1 />,
              value: tokenSymbol,
              hint: tokenIssue,
              badge: tokenIssue ? "!" : undefined,
              submenu: {
                title: "Network token",
                sections: [{ items: tokenDrawerItems }],
              },
            },
          ]
        : [],
    },
    ...(networkType === "mainnet"
      ? [
          {
            label: "Account",
            items: [
              {
                label: "Change password",
                icon: <KeyIconV1 />,
                onClick: () => navigate("/update-password"),
              },
            ],
          },
        ]
      : []),
    {
      label: "Help and settings",
      items: [
        {
          label: "About",
          icon: <InfoIconV1 />,
          external: true,
          onClick: () => window.open("https://kea-valley.com/", "_blank"),
        },
        {
          label: "Logout",
          icon: <LogOutIconV1 />,
          variant: "danger",
          onClick: handleLogout,
        },
      ],
    },
  ];

  return (
    <>
      <header className="gp-topbar">
        <div className="gp-topbar__brand">
          <BrandLogo />
          <RolePill label="Treasury" variant="pink" />
        </div>

        <div className="gp-nav-actions">
          <button
            className={`refresh_mini-btn${Boolean(isFetching) ? " refresh_mini-btn--spinning" : ""}`}
            aria-label="Refresh data"
            onClick={handleRefresh}
            disabled={Boolean(isFetching)}
          >
            <RefreshIconV1 size={16} />
          </button>
          {tokenSymbol && (
            <span
              className={`gp-token-label${tokenIssue ? " gp-token-label--warn" : ""}`}
              title={tokenIssue ?? `Amounts shown in ${tokenSymbol}`}
            >
              {tokenSymbol}
            </span>
          )}
          <div className={`gp-chip-network gp-chip-network--${network.value}`}>
            {/* Separate span so the narrow-viewport rule can drop the prefix and
                keep just "Mainnet" / "Testnet". */}
            <span className="gp-chip-network__prefix">{NETWORK_NAME}</span>
            {network.shortLabel}
          </div>
          <button
            className="gp-nav-menu-btn"
            aria-label="Settings"
            aria-expanded={settingsOpen}
            aria-haspopup="dialog"
            onClick={() => setSettingsOpen(true)}
          >
            <MenuIconV1 />
          </button>
        </div>
      </header>

      <SettingsDrawer
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        sections={settingsSections}
      />
    </>
  );
}

export default IssuerNav;
